import { Fragment, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { setAsgDesired, startTpe, scheduleTpe, fetchTickersAdmin } from "../api/client";
import type { TradingMode } from "../api/client";

// datetime-local value (local wall-clock, no tz) for `now + hoursAhead`,
// formatted YYYY-MM-DDTHH:mm and floored to the minute.
const localInputValue = (hoursAhead: number) => {
  const d = new Date(Date.now() + hoursAhead * 3600_000);
  d.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Configure-and-launch modal for a broad TPE search across all enabled
 * tickers. Defaults to the recommended *thorough* search (deep budget so
 * TPE has room to actually find good configs). On launch it (1) sets the
 * ASG desired capacity, then (2) starts the search with the workflow
 * overrides — both via existing endpoints.
 *
 * Colour: blue accents only; status is text, never colour-alone (protan-safe).
 */
const BLUE = "#3b82f6";
const ORANGE = "#f59e0b";
const GREY = "#64748b";

// Trading-state badge styling. Protan-safe: each state carries a TEXT label,
// never colour alone. Blue = full, orange = sells-only, grey = disabled.
const MODE_STYLE: Record<TradingMode, { label: string; color: string }> = {
  full: { label: "Full", color: BLUE },
  sells_only: { label: "Sells-only", color: ORANGE },
  disabled: { label: "Disabled", color: GREY },
};

const SLOTS_PER_INSTANCE = 8;     // 2-vCPU tasks on a 4xlarge (16 vCPU)
const MAP_CAP = 1000;             // max_backtest_concurrency after the CDK bump
const P1_TASK_MIN = 2.2;          // empirical Phase-1 (25k) task minutes
const P2_TASK_MIN = 4.4;          // ~2x for Phase-2 (50k)

type Fields = {
  num_rounds: number;
  trials_per_round: number;
  phase1_timesteps: number;
  phase2_timesteps: number;
  phase2_top_n: number;
  phase2_replicates: number;
  desired_instances: number;
};

const DEFAULTS: Fields = {
  num_rounds: 8,
  trials_per_round: 120,
  phase1_timesteps: 50000,
  phase2_timesteps: 50000,
  phase2_top_n: 30,
  phase2_replicates: 7,
  desired_instances: 40,
};

const FIELD_META: { key: keyof Fields; label: string; hint: string; min: number; max: number }[] = [
  { key: "num_rounds", label: "TPE rounds", hint: "Sequential adaptive rounds (round 1 is cold; 2+ are informed). More = better search.", min: 1, max: 12 },
  { key: "trials_per_round", label: "Trials / round / ticker", hint: "Configs explored per round. rounds × this = total configs/ticker.", min: 10, max: 300 },
  { key: "phase1_timesteps", label: "Phase-1 timesteps", hint: "Training length per exploration trial. Higher = better-trained, slower.", min: 5000, max: 200000 },
  { key: "phase2_timesteps", label: "Phase-2 timesteps", hint: "Training length per finalist replicate.", min: 5000, max: 200000 },
  { key: "phase2_top_n", label: "Phase-2 top-N", hint: "How many finalists get robust multi-replicate validation.", min: 1, max: 100 },
  { key: "phase2_replicates", label: "Phase-2 replicates", hint: "Seeds per finalist (consistency check).", min: 1, max: 15 },
  { key: "desired_instances", label: "Desired instances", hint: "Sets ASG desired (also raises max to match). Drives speed, not search quality. ~8 slots/4xl box.", min: 1, max: 80 },
];

export function TpeSearchModal({ open, onClose, onLaunched }: {
  open: boolean;
  onClose: () => void;
  onLaunched: (runId: string) => void;
}) {
  const [f, setF] = useState<Fields>(DEFAULTS);
  const [status, setStatus] = useState<"idle" | "working" | "error">("idle");
  const [msg, setMsg] = useState<string>("");
  // Run immediately vs schedule for a future time. `fireAtLocal` is a
  // datetime-local string (browser local time); converted to UTC ISO on submit.
  const [mode, setMode] = useState<"now" | "schedule">("now");
  const [fireAtLocal, setFireAtLocal] = useState<string>(() => localInputValue(1));
  // Ticker subset. `null` = not yet initialised; defaulted to all
  // backtestable tickers once the roster loads. Drives `restrict_tickers`.
  const [selected, setSelected] = useState<Set<string> | null>(null);

  const tickersQ = useQuery({
    queryKey: ["tickers-admin"],
    queryFn: fetchTickersAdmin,
    enabled: open,
  });
  const allRows = useMemo(() => tickersQ.data?.tickers ?? [], [tickersQ.data]);
  // Only backtest-enabled tickers can be searched (the start path validates
  // restrict_tickers against the enabled-for-backtest set).
  const backtestable = useMemo(() => allRows.filter((r) => r.enabled_for_backtest), [allRows]);

  // Default-select all backtestable tickers once the roster arrives.
  useEffect(() => {
    if (selected === null && backtestable.length) {
      setSelected(new Set(backtestable.map((r) => r.ticker)));
    }
  }, [backtestable, selected]);

  const sel = selected ?? new Set<string>();
  const selCount = sel.size;

  const est = useMemo(() => {
    const n = Math.max(1, selCount);
    const p1Trials = f.num_rounds * f.trials_per_round * n;
    const p2Jobs = f.phase2_top_n * f.phase2_replicates * n;
    const conc = Math.max(1, Math.min(f.desired_instances * SLOTS_PER_INSTANCE, MAP_CAP));
    // timesteps scale the per-task minutes off the 25k baseline
    const p1Min = P1_TASK_MIN * (f.phase1_timesteps / 25000);
    const p2Min = P2_TASK_MIN * (f.phase2_timesteps / 50000);
    const hrs = (p1Trials * p1Min + p2Jobs * p2Min) / conc / 60 + 0.8; // +overhead
    return { p1Trials, p2Jobs, conc, hrs };
  }, [f, selCount]);

  if (!open) return null;

  const toggle = (t: string) =>
    setSelected((prev) => {
      const next = new Set(prev ?? []);
      next.has(t) ? next.delete(t) : next.add(t);
      return next;
    });
  const selectAll = () => setSelected(new Set(backtestable.map((r) => r.ticker)));
  const selectNone = () => setSelected(new Set());
  const selectFullOnly = () =>
    setSelected(new Set(backtestable.filter((r) => r.trading_mode === "full").map((r) => r.ticker)));

  const set = (k: keyof Fields, v: string) =>
    setF((prev) => ({ ...prev, [k]: Number(v) }));

  const featureOverrides = {
    num_rounds: f.num_rounds,
    trials_per_round: f.trials_per_round,
    phase1_timesteps: f.phase1_timesteps,
    phase2_timesteps: f.phase2_timesteps,
    phase2_top_n: f.phase2_top_n,
    phase2_replicates: f.phase2_replicates,
  };

  const launch = async () => {
    const restrictTickers = Array.from(sel);
    if (restrictTickers.length === 0) {
      setStatus("error");
      setMsg("Select at least one ticker to search.");
      return;
    }
    setStatus("working");
    try {
      if (mode === "schedule") {
        // No setAsgDesired here: the run fires later and ECS managed scaling
        // (target 100%) scales the cluster on task demand at that time.
        const d = new Date(fireAtLocal);
        if (isNaN(d.getTime()) || d.getTime() <= Date.now()) {
          setStatus("error");
          setMsg("Pick a date/time in the future.");
          return;
        }
        setMsg("Scheduling search…");
        const res = await scheduleTpe({ fireAt: d.toISOString(), featureOverrides, restrictTickers });
        setStatus("idle");
        setMsg(`Scheduled ${restrictTickers.length} ticker(s) for ${d.toLocaleString()} — will start automatically (queue ${res.queue_id}).`);
        return;
      }

      setMsg("Setting cluster size…");
      await setAsgDesired(f.desired_instances);
      setMsg("Starting search…");
      const res = await startTpe({ featureOverrides, restrictTickers });
      const runId = (res as { run_id?: string }).run_id;
      if (runId) {
        onLaunched(runId);
        onClose();
      } else {
        // Gated/queued: no run_id yet (a slot wasn't free).
        setStatus("idle");
        setMsg("Queued — will start when a TPE slot frees (cap reached).");
      }
    } catch (e) {
      setStatus("error");
      setMsg(String((e as Error).message || e));
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(2,6,23,0.7)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#0b1220", border: "1px solid #1e293b", borderRadius: 8,
        width: "min(560px, 96vw)", maxHeight: "92vh", overflowY: "auto", padding: 20, color: "#e2e8f0",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>Configure &amp; launch TPE search</h3>
          <button onClick={onClose} style={{ background: "#1e293b", color: "#cbd5e1", border: "none", borderRadius: 4, padding: "4px 10px", cursor: "pointer" }}>Close</button>
        </div>
        <p style={{ fontSize: 12.5, color: "#94a3b8", lineHeight: 1.5, marginTop: 8 }}>
          Auto-search across the tickers you pick below — TPE chooses the feature preset,
          toggles indicators, and tunes hyperparameters itself. Defaults are the
          recommended thorough search.
        </p>

        {/* Run now vs schedule. Same params either way; scheduling enqueues a
            pending run that fires at the chosen time through the same gate. */}
        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          {([["now", "Run now"], ["schedule", "Schedule for later"]] as const).map(([m, label]) => (
            <button key={m} onClick={() => setMode(m)} disabled={status === "working"}
              style={{
                flex: 1, padding: "7px 10px", fontSize: 13, cursor: "pointer", borderRadius: 4,
                background: mode === m ? "#0c1e3d" : "#0f172a",
                color: mode === m ? "#bfdbfe" : "#94a3b8",
                border: `1px solid ${mode === m ? BLUE : "#334155"}`,
                fontWeight: mode === m ? 600 : 400,
              }}>
              {label}
            </button>
          ))}
        </div>

        {mode === "schedule" && (
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 13 }}>Start at (your local time)</label>
            <input
              type="datetime-local"
              value={fireAtLocal}
              min={localInputValue(0)}
              onChange={(e) => setFireAtLocal(e.target.value)}
              style={{ background: "#0f172a", color: "#e2e8f0", border: "1px solid #334155", borderRadius: 4, padding: "6px 8px", fontSize: 13 }}
            />
            <span style={{ color: "#64748b", fontSize: 11 }}>
              Enqueued now, launches at this time (up to 14 days out). Cluster scales itself then —
              the “desired instances” field below only warm-starts a Run-now launch.
            </span>
          </div>
        )}

        {/* Ticker picker — choose the subset to search and see each ticker's
            trading state. Only backtest-enabled tickers are selectable. */}
        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Tickers to search</label>
            <span style={{ fontSize: 11, color: GREY }}>{selCount} selected</span>
          </div>
          <div style={{ display: "flex", gap: 6, margin: "6px 0" }}>
            {([["All", selectAll], ["None", selectNone], ["Full only", selectFullOnly]] as const).map(([label, fn]) => (
              <button key={label} type="button" onClick={fn} disabled={status === "working"}
                style={{ background: "#0f172a", color: "#cbd5e1", border: "1px solid #334155", borderRadius: 4, padding: "3px 10px", fontSize: 11.5, cursor: "pointer" }}>
                {label}
              </button>
            ))}
          </div>
          {tickersQ.isLoading ? (
            <div style={{ fontSize: 12.5, color: GREY }}>Loading tickers…</div>
          ) : tickersQ.error ? (
            <div style={{ fontSize: 12.5, color: ORANGE }}>⚠ Failed to load tickers.</div>
          ) : (
            <div style={{ maxHeight: 170, overflowY: "auto", border: "1px solid #1e293b", borderRadius: 4, padding: 6, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 12px" }}>
              {allRows.map((r) => {
                const bt = r.enabled_for_backtest;
                const ms = MODE_STYLE[r.trading_mode] ?? MODE_STYLE.full;
                return (
                  <label key={r.ticker}
                    title={bt ? `${ms.label}${r.trading_mode !== "disabled" ? "" : " — not traded"}` : "Not enabled for backtest — can't be searched"}
                    style={{ display: "flex", alignItems: "center", gap: 7, padding: "2px 4px", opacity: bt ? 1 : 0.45, cursor: bt ? "pointer" : "not-allowed" }}>
                    <input type="checkbox" disabled={!bt} checked={bt && sel.has(r.ticker)}
                      onChange={() => toggle(r.ticker)} />
                    <span style={{ fontWeight: 600, minWidth: 52, fontSize: 12.5 }}>{r.ticker}</span>
                    <span style={{ fontSize: 10.5, border: `1px solid ${ms.color}`, color: ms.color, borderRadius: 4, padding: "0 5px", whiteSpace: "nowrap" }}>{ms.label}</span>
                    {!bt && <span style={{ fontSize: 10, color: GREY }}>no backtest</span>}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: "10px 12px", alignItems: "center", marginTop: 12 }}>
          {FIELD_META.map((m) => (
            <Fragment key={m.key}>
              <label title={m.hint} style={{ fontSize: 13 }}>
                {m.label}
                <span style={{ color: "#64748b", display: "block", fontSize: 11 }}>{m.hint}</span>
              </label>
              <input
                type="number" min={m.min} max={m.max} value={f[m.key]}
                onChange={(e) => set(m.key, e.target.value)}
                style={{ background: "#0f172a", color: "#e2e8f0", border: "1px solid #334155", borderRadius: 4, padding: "6px 8px", fontSize: 13, width: "100%" }}
              />
            </Fragment>
          ))}
        </div>

        <div className="card" style={{ marginTop: 14, background: "#0c1e3d", border: `1px solid ${BLUE}`, fontSize: 12.5, lineHeight: 1.6 }}>
          <strong>Rough estimate</strong> ({selCount} selected ticker{selCount === 1 ? "" : "s"}):<br />
          ~{est.p1Trials.toLocaleString()} Phase-1 trials, {est.p2Jobs.toLocaleString()} Phase-2 jobs ·
          ~{est.conc} concurrent · <strong style={{ color: BLUE }}>~{est.hrs.toFixed(1)} h</strong> wall-clock
          <div style={{ color: "#64748b", fontSize: 11, marginTop: 4 }}>
            Speed depends on instances (set above) capped by the Map limit ({MAP_CAP}) and the Spot vCPU quota.
            Cost ≈ fixed compute regardless of speed.
          </div>
        </div>

        {msg && (
          <div style={{ marginTop: 12, fontSize: 13, color: status === "error" ? ORANGE : "#cbd5e1" }}>
            {status === "error" ? "⚠ " : ""}{msg}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
          <button onClick={onClose} disabled={status === "working"}
            style={{ background: "#0f172a", color: "#cbd5e1", border: "1px solid #334155", borderRadius: 4, padding: "8px 14px", cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={launch} disabled={status === "working"}
            style={{ background: BLUE, color: "#04122e", border: "none", borderRadius: 4, padding: "8px 16px", fontWeight: 600, cursor: status === "working" ? "default" : "pointer", opacity: status === "working" ? 0.7 : 1 }}>
            {status === "working"
              ? (mode === "schedule" ? "Scheduling…" : "Launching…")
              : (mode === "schedule" ? "Schedule" : `Launch now (${f.desired_instances} inst)`)}
          </button>
        </div>
      </div>
    </div>
  );
}
